import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, Scale, FileStack, Newspaper, Layers, Users2, FileText,
  FolderOpen, Image, Images, Inbox, Settings, ScrollText, LogOut, ExternalLink, Shield, ShieldCheck, Grid3x3, Link2, HeartHandshake, Search, Type, Telescope, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pils", label: "PIL Management", icon: Scale },
  { to: "/admin/issues", label: "Under the Lens", icon: Telescope },
  { to: "/admin/press", label: "Press Releases", icon: Newspaper },
  { to: "/admin/coverage", label: "Media Coverage", icon: Link2 },
  { to: "/admin/media-logos", label: "As Seen In (Logos)", icon: Image },
  { to: "/admin/media-clips", label: "Media Clips", icon: Images },
  { to: "/admin/areas", label: "Areas of Work", icon: Grid3x3 },
  { to: "/admin/initiatives", label: "Initiatives", icon: Layers },
  { to: "/admin/leadership", label: "Leadership", icon: Users2 },
  { to: "/admin/pages", label: "Pages", icon: FileText },
  { to: "/admin/documents", label: "Document Centre", icon: FolderOpen },
  { to: "/admin/official-documents", label: "Official Documents", icon: ShieldCheck },
  { to: "/admin/media", label: "Media Library", icon: Image },
  { to: "/admin/enquiries", label: "Contact Enquiries", icon: Inbox },
  { to: "/admin/donations", label: "Donations", icon: HeartHandshake },
  { to: "/admin/users", label: "Users & Permissions", icon: Shield },
  { to: "/admin/settings", label: "Website Settings", icon: Settings },
  { to: "/admin/typography", label: "Typography", icon: Type },
  { to: "/admin/seo", label: "SEO & Link Previews", icon: Search },
  { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const location = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-npst-navyDeep text-white flex items-center justify-between px-4" data-testid="admin-mobile-bar">
        <Link to="/admin" className="flex items-center gap-2"><img src="/npst-logo.webp" alt="NPST" className="w-8 h-8 object-contain bg-white rounded-sm p-0.5" /><span className="font-display text-xs font-bold tracking-wide">NPST <span className="text-npst-gold">ADMIN</span></span></Link>
        <button onClick={() => setOpen(!open)} aria-label="Admin menu" data-testid="admin-mobile-menu-btn" className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-white/10">{open ? <X size={20} /> : <Menu size={20} />}</button>
      </div>
      {open && <div className="lg:hidden fixed inset-0 z-40 bg-npst-navyDeep/50 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" />}
      <aside className={`w-64 bg-npst-navyDeep text-slate-300 flex flex-col fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-out lg:translate-x-0 ${open ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`} data-testid="admin-sidebar">
        <Link to="/admin" className="flex items-center gap-3 px-5 h-16 border-b border-white/10">
          <img src="/npst-logo.webp" alt="NPST" className="w-9 h-9 object-contain bg-white rounded-sm p-0.5" />
          <div className="font-display text-xs font-bold text-white tracking-wide leading-tight">NPST<br /><span className="text-[9px] text-npst-gold tracking-[0.2em]">ADMIN</span></div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) => `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] font-medium transition-colors ${isActive ? "bg-npst-gold/15 text-npst-gold before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-npst-gold" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <n.icon size={17} className="shrink-0 transition-transform duration-200 group-hover:scale-110" /> <span className="truncate">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 text-[13px] text-slate-400 hover:text-white"><ExternalLink size={15} /> View Public Site</a>
          <div className="px-3 py-2 text-[11px] text-slate-500">{user?.name} · <span className="uppercase">{user?.role?.replace("_", " ")}</span></div>
          <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-3 px-3 py-2 text-[13px] text-slate-400 hover:text-red-300 w-full"><LogOut size={15} /> Sign out</button>
        </div>
      </aside>
      <main className="admin-main flex-1 min-w-0 lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <div key={location.pathname} className="route-fade"><Outlet /></div>
      </main>
    </div>
  );
}
